import { useEffect, useRef, useState } from "react";
import Notiflix from "notiflix";
import useIndex from "./useIndex";
import {
  confirmWompi,
  getInvoiceStatus,
  startCheckout,
} from "../data/api/checkout/checkout";

const PAYMENT_FAILED_STATUSES = ["DECLINED", "ERROR", "VOIDED", "EXPIRED"];

const showApprovedAlert = (movesAwarded) => {
  if (!movesAwarded) {
    Notiflix.Report.success(
      "Pago aprobado",
      "Tu pago fue confirmado correctamente por Wompi.",
      "Continuar"
    );
    return;
  }
  const playsLabel = movesAwarded === 1 ? "jugada" : "jugadas";
  Notiflix.Report.success(
    "¡Pago aprobado!",
    `
      <div style="text-align:center;">
        <div style="font-size:0.95rem;color:rgba(0,0,0,0.7);margin-bottom:14px;">
          Tu pago fue confirmado correctamente.
        </div>
        <div style="background:#e8f5e9;border:2px solid #66bb6a;border-radius:14px;padding:18px 14px;">
          <div style="font-size:0.85rem;font-weight:700;color:#2e7d32;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:6px;">
            Jugadas agregadas
          </div>
          <div style="font-size:3rem;font-weight:900;color:#1b5e20;line-height:1;">
            +${movesAwarded}
          </div>
          <div style="font-size:0.95rem;font-weight:700;color:#2e7d32;margin-top:6px;">
            ${playsLabel} disponibles para jugar
          </div>
        </div>
      </div>
    `,
    "Continuar",
    null,
    { plainText: false, messageMaxLength: 2000 }
  );
};

/**
 * Encapsula el flujo completo del widget de Wompi:
 *   1. POST /api/checkout para crear la invoice y obtener el payload firmado.
 *   2. Abre el widget con `new window.WidgetCheckout`.
 *   3. Al cerrarse: confirma contra la API de Wompi via /checkout/{uuid}/confirm.
 *   4. Si todavía está pendiente, hace polling al status hasta confirmar.
 *
 * Toda la UX (Notiflix, validateUser) se maneja internamente para que los
 * consumidores solo invoquen `openCheckout(payload)` sin duplicar lógica.
 *
 * @param {object} options
 * @param {() => void} [options.closeParentModal]  Callback que se ejecuta
 *        antes de abrir el widget (típicamente para cerrar el Modal de MUI que
 *        contiene el botón, pues bloquea el scroll del body que el widget de
 *        Wompi necesita).
 * @param {{ title?: string, message?: string }} [options.successAlert]  Permite
 *        personalizar el alert mostrado al aprobarse el pago. Útil para
 *        donaciones (mensaje de agradecimiento) en lugar del genérico de pago.
 */
export default function useWompiCheckout({ closeParentModal, successAlert } = {}) {
  const { token, validateUser } = useIndex();
  const [loading, setLoading] = useState(false);
  const pollingRef = useRef(null);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const pollInvoiceStatus = (uuid, maxAttempts = 15) => {
    let attempts = 0;
    stopPolling();

    pollingRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await getInvoiceStatus(uuid);
        const txResponse = data?.data?.transaction_response;

        if (txResponse === "APPROVED") {
          stopPolling();
          // Solo refrescar el usuario si hay sesión (en flujos anónimos
          // como donación pública, no hay user a refrescar).
          if (token) validateUser?.();
          if (successAlert?.title) {
            Notiflix.Report.success(
              successAlert.title,
              successAlert.message ?? "Tu pago fue procesado correctamente.",
              "Continuar"
            );
          } else {
            Notiflix.Report.success(
              "Pago aprobado",
              "Tu pago fue procesado correctamente.",
              "Continuar"
            );
          }
          return;
        }
        if (PAYMENT_FAILED_STATUSES.includes(txResponse)) {
          stopPolling();
          Notiflix.Report.failure(
            "Pago no completado",
            `La transacción quedó en estado ${txResponse}.`,
            "OK"
          );
          return;
        }
        if (attempts >= maxAttempts) {
          stopPolling();
          Notiflix.Notify.info(
            "Aún estamos confirmando tu pago, actualizaremos en unos minutos."
          );
        }
      } catch (e) {
        if (attempts >= maxAttempts) stopPolling();
      }
    }, 2000);
  };

  // Cleanup si el consumidor se desmonta con polling activo.
  useEffect(() => () => stopPolling(), []);

  /**
   * @param {object} payload  Payload para el endpoint default (/api/checkout).
   *        Se ignora si se pasa `options.startFn` con su propia lógica.
   * @param {object} [options]
   * @param {(token, payload) => Promise} [options.startFn]  Función custom
   *        para iniciar el checkout. Útil para flujos que tienen su propio
   *        endpoint server-side (e.g., contratos). Debe resolver al mismo
   *        shape que startCheckout: `{ data: { data: { invoice, wompi } } }`.
   */
  const openCheckout = async (payload, options = {}) => {
    if (!window.WidgetCheckout) {
      Notiflix.Notify.failure(
        "No se pudo cargar el widget de Wompi. Recarga la página e intenta de nuevo."
      );
      return;
    }

    setLoading(true);
    try {
      const startFn = options.startFn ?? startCheckout;
      const { data } = await startFn(token, payload);
      const wompi = data?.data?.wompi;
      const invoice = data?.data?.invoice;
      if (!wompi || !invoice) {
        throw new Error("Respuesta inválida del servidor.");
      }

      const checkout = new window.WidgetCheckout({
        currency: wompi.currency,
        amountInCents: wompi.amount_in_cents,
        reference: wompi.reference,
        publicKey: wompi.public_key,
        signature: { integrity: wompi.signature },
        ...(wompi.expiration_time && { expirationTime: wompi.expiration_time }),
      });

      // Cerrar el modal padre antes de abrir el widget: MUI Modal bloquea el
      // scroll del body y eso impide desplazarse dentro del widget de Wompi.
      closeParentModal?.();

      checkout.open(async (result) => {
        const transaction = result?.transaction;

        if (transaction?.id) {
          try {
            const { data } = await confirmWompi(token, invoice.uuid, transaction.id);
            const txResponse = data?.data?.transaction_response;
            const movesAwarded = data?.data?.moves_awarded;

            if (txResponse === "APPROVED") {
              if (token) validateUser?.();
              if (successAlert?.title) {
                Notiflix.Report.success(
                  successAlert.title,
                  successAlert.message ?? "Tu pago fue confirmado correctamente.",
                  "Continuar"
                );
              } else {
                showApprovedAlert(movesAwarded);
              }
              return;
            }
            if (PAYMENT_FAILED_STATUSES.includes(txResponse)) {
              Notiflix.Report.failure(
                "Pago no completado",
                `La transacción quedó en estado ${txResponse}.`,
                "OK"
              );
              return;
            }
            // Estado aún pendiente -> caemos al polling esperando el webhook.
            Notiflix.Notify.info("Estamos confirmando el pago...");
            pollInvoiceStatus(invoice.uuid);
          } catch (e) {
            Notiflix.Notify.warning(
              "No pudimos confirmar el pago de inmediato, reintentando..."
            );
            pollInvoiceStatus(invoice.uuid);
          }
        } else {
          Notiflix.Notify.info("No se completó ninguna transacción.");
        }
      });
    } catch (error) {
      Notiflix.Report.failure(
        "Error iniciando el pago",
        error?.response?.data?.message || error.message || "Intenta de nuevo.",
        "OK"
      );
    } finally {
      setLoading(false);
    }
  };

  return { openCheckout, loading };
}
